import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Briefcase,
  Plus,
  Trash2,
  Edit,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Receipt,
  FileText,
  Download,
  Upload
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

import { BASE_URL as API } from '../App';

export default function EmploymentTypesPanel() {
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [scheduleCLineItems, setScheduleCLineItems] = useState([]);
  const [expandedType, setExpandedType] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  
  // Income source management
  const [typeSources, setTypeSources] = useState({});
  const [newSourceName, setNewSourceName] = useState({});
  
  // Expense category management
  const [typeCategories, setTypeCategories] = useState({});
  const [newCategoryName, setNewCategoryName] = useState({});
  
  // Schedule C mapping management
  const [scheduleCMappings, setScheduleCMappings] = useState({});
  const [showScheduleCDialog, setShowScheduleCDialog] = useState(false);
  const [selectedScheduleCItem, setSelectedScheduleCItem] = useState(null);
  const [scheduleCFormData, setScheduleCFormData] = useState({
    category_type: 'expense',
    category_name: '',
    schedule_c_line: '',
    schedule_c_description: ''
  });

  useEffect(() => {
    fetchEmploymentTypes();
    fetchScheduleCLineItems();
  }, []);

  const fetchScheduleCLineItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/schedule-c-line-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScheduleCLineItems(response.data);
    } catch (error) {
      console.error('Failed to fetch Schedule C line items:', error);
      toast.error('Failed to load Schedule C line items');
    }
  };

  const fetchEmploymentTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/employment-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmploymentTypes(response.data);
    } catch (error) {
      toast.error('Failed to fetch employment types');
    }
  };

  const fetchTypeIncomeSources = async (typeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/employment-types/${typeId}/income-sources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTypeSources(prev => ({ ...prev, [typeId]: response.data }));
      return response.data; // Return the data for immediate use
    } catch (error) {
      toast.error('Failed to fetch income sources');
      return [];
    }
  };

  const fetchTypeExpenseCategories = async (typeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/employment-types/${typeId}/expense-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTypeCategories(prev => ({ ...prev, [typeId]: response.data }));
      return response.data; // Return the data for immediate use
    } catch (error) {
      toast.error('Failed to fetch expense categories');
      return [];
    }
  };

  const fetchScheduleCMappings = async (typeId, sources = null, categories = null) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/schedule-c-mappings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter mappings for this specific employment type
      const typeMappings = response.data.filter(m => m.employment_type_id === typeId);
      
      setScheduleCMappings(prev => ({ ...prev, [typeId]: typeMappings }));
    } catch (error) {
      console.error('Failed to fetch Schedule C mappings', error);
    }
  };

  const handleTypeExpand = async (typeId) => {
    if (expandedType === typeId) {
      setExpandedType(null);
    } else {
      setExpandedType(typeId);
      // Fetch sources, categories, and Schedule C mappings when expanding
      const sources = await fetchTypeIncomeSources(typeId);
      const categories = await fetchTypeExpenseCategories(typeId);
      // Fetch mappings with the fresh data
      await fetchScheduleCMappings(typeId, sources, categories);
    }
  };

  const handleCreateType = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/admin/employment-types`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employment type created successfully');
      setShowCreateDialog(false);
      setFormData({ name: '', description: '' });
      fetchEmploymentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create employment type');
    }
  };

  const handleUpdateType = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/admin/employment-types/${selectedType.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employment type updated successfully');
      setShowEditDialog(false);
      setSelectedType(null);
      setFormData({ name: '', description: '' });
      fetchEmploymentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employment type');
    }
  };

  const handleDeleteType = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/employment-types/${selectedType.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employment type deleted successfully');
      setShowDeleteDialog(false);
      setSelectedType(null);
      fetchEmploymentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employment type');
    }
  };

  const handleAddIncomeSource = async (typeId) => {
    if (!newSourceName[typeId]?.trim()) {
      toast.error('Please enter a source name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/admin/employment-types/${typeId}/income-sources`, 
        { name: newSourceName[typeId] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Income source added successfully');
      setNewSourceName(prev => ({ ...prev, [typeId]: '' }));
      fetchTypeIncomeSources(typeId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add income source');
    }
  };

  const handleDeleteIncomeSource = async (typeId, sourceId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/employment-types/${typeId}/income-sources/${sourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Income source removed successfully');
      fetchTypeIncomeSources(typeId);
    } catch (error) {
      toast.error('Failed to remove income source');
    }
  };

  const handleAddExpenseCategory = async (typeId) => {
    if (!newCategoryName[typeId]?.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/admin/employment-types/${typeId}/expense-categories`, 
        { name: newCategoryName[typeId] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Expense category added successfully');
      setNewCategoryName(prev => ({ ...prev, [typeId]: '' }));
      const categories = await fetchTypeExpenseCategories(typeId);
      await fetchScheduleCMappings(typeId, null, categories);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add expense category');
    }
  };

  const handleDeleteExpenseCategory = async (typeId, categoryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/employment-types/${typeId}/expense-categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Expense category removed successfully');
      const categories = await fetchTypeExpenseCategories(typeId);
      await fetchScheduleCMappings(typeId, null, categories);
    } catch (error) {
      toast.error('Failed to remove expense category');
    }
  };

  const handleAddScheduleCMapping = async (typeId) => {
    if (!scheduleCFormData.category_name || !scheduleCFormData.schedule_c_line) {
      toast.error('Please select both a category and Schedule C line');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/schedule-c-mappings`, scheduleCFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Schedule C mapping added successfully');
      
      // Immediately update state to show the new mapping and checkmark
      setScheduleCMappings(prev => ({
        ...prev,
        [typeId]: [...(prev[typeId] || []), response.data]
      }));
      
      setShowScheduleCDialog(false);
      setScheduleCFormData({
        category_type: 'expense',
        category_name: '',
        schedule_c_line: '',
        schedule_c_description: ''
      });
      
      // Re-fetch with current state sources and categories to ensure consistency
      await fetchScheduleCMappings(typeId, typeSources[typeId], typeCategories[typeId]);
    } catch (error) {
      console.error('Schedule C mapping error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add Schedule C mapping');
    }
  };

  const handleDeleteScheduleCMapping = async (typeId, categoryType, categoryName) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/schedule-c-mappings/${typeId}/${categoryType}/${categoryName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Schedule C mapping removed successfully');
      
      // Immediately update state to remove the mapping and clear checkmark
      setScheduleCMappings(prev => ({
        ...prev,
        [typeId]: (prev[typeId] || []).filter(
          m => !(m.category_type === categoryType && m.category_name === categoryName)
        )
      }));
      
      // Re-fetch with current state sources and categories to ensure consistency
      await fetchScheduleCMappings(typeId, typeSources[typeId], typeCategories[typeId]);
    } catch (error) {
      toast.error('Failed to remove Schedule C mapping');
    }
  };

  const openScheduleCDialog = (typeId, categoryType) => {
    setSelectedScheduleCItem({ typeId, categoryType });
    setScheduleCFormData({
      ...scheduleCFormData,
      category_type: categoryType,
      category_name: '',
      schedule_c_line: '',
      schedule_c_description: ''
    });
    setShowScheduleCDialog(true);
  };

  const openEditDialog = (type) => {
    setSelectedType(type);
    setFormData({ name: type.name, description: type.description || '' });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (type) => {
    setSelectedType(type);
    setShowDeleteDialog(true);
  };

  const handleDownloadEmploymentType = async (type) => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch sources, categories, and mappings for this type
      const sources = typeSources[type.id] || [];
      const categories = typeCategories[type.id] || [];
      const mappings = scheduleCMappings[type.id] || [];

      // Create Excel data
      const excelData = [];
      excelData.push(['Item Type', 'Item Name', 'Schedule C Line #']);

      // Add ALL income sources first
      for (const source of sources) {
        const mapping = mappings.find(m => m.category_type === 'income' && m.category_name === source.name);
        excelData.push([
          'income_source',
          source.name,
          mapping ? `Line ${mapping.schedule_c_line}` : ''
        ]);
      }

      // Then add ALL expense categories
      for (const category of categories) {
        const mapping = mappings.find(m => m.category_type === 'expense' && m.category_name === category.name);
        excelData.push([
          'expense_category',
          category.name,
          mapping ? `Line ${mapping.schedule_c_line}` : ''
        ]);
      }

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 20 },  // Item Type
        { wch: 50 },  // Item Name
        { wch: 20 }   // Schedule C Line #
      ];
      
      XLSX.utils.book_append_sheet(wb, ws, 'Template');
      
      // Generate Excel file and download
      XLSX.writeFile(wb, `${type.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);

      toast.success(`${type.name} template downloaded!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download template');
    }
  };

  const handleCleanupOrphanedMappings = async () => {
    const confirmed = window.confirm(
      `🧹 CLEANUP ORPHANED SCHEDULE C MAPPINGS?\n\n` +
      `This will delete mappings from deleted employment types.\n` +
      `These orphaned mappings may be showing for all users.\n\n` +
      `This is safe and will NOT delete valid mappings.\n\n` +
      `Click OK to proceed.`
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      toast.info('Cleaning up orphaned mappings...');

      const response = await axios.post(`${API}/api/admin/cleanup-orphaned-mappings`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.message || 'Cleanup complete!');
      
      // Refresh all employment types
      await fetchEmploymentTypes();
      
    } catch (error) {
      console.error('Cleanup error:', error);
      toast.error(error.response?.data?.detail || 'Cleanup failed');
    }
  };

  const handleDeleteAllMappings = async (type) => {
    const mappings = scheduleCMappings[type.id] || [];
    
    if (mappings.length === 0) {
      toast.info('No Schedule C mappings to delete for this employment type.');
      return;
    }

    const confirmed = window.confirm(
      `⚠️ DELETE ALL SCHEDULE C MAPPINGS FOR "${type.name}"?\n\n` +
      `This will permanently delete ${mappings.length} Schedule C mapping(s).\n` +
      `Income sources and expense categories will NOT be deleted.\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Click OK to confirm.`
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      let deletedCount = 0;
      let errorCount = 0;

      toast.info(`Deleting ${mappings.length} mappings from ${type.name}...`);

      // Delete all mappings for this employment type using admin endpoint
      try {
        await axios.delete(`${API}/api/admin/schedule-c-mappings/${type.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deletedCount = mappings.length;
        toast.success(`Successfully deleted all ${deletedCount} mappings from ${type.name}!`);
      } catch (err) {
        console.error('Error deleting mappings:', err);
        toast.error('Failed to delete mappings. Check console for details.');
        errorCount = mappings.length;
      }

      // Clear the mappings state immediately to remove green checkmarks
      setScheduleCMappings(prev => ({ ...prev, [type.id]: [] }));
      
      // Then refresh data from server to ensure consistency
      await fetchScheduleCMappings(type.id, typeSources[type.id], typeCategories[type.id]);
      
    } catch (error) {
      console.error('Delete all mappings error:', error);
      toast.error('Failed to delete mappings');
    }
  };

  const handleClearAllItems = async (type) => {
    const sources = typeSources[type.id] || [];
    const categories = typeCategories[type.id] || [];
    const totalItems = sources.length + categories.length;

    if (totalItems === 0) {
      toast.info('Nothing to clear - this employment type has no items.');
      return;
    }

    const confirmed = window.confirm(
      `⚠️ DELETE ALL INCOME SOURCES & EXPENSE CATEGORIES FROM "${type.name}"?\n\n` +
      `This will permanently delete:\n` +
      `• ${sources.length} income source(s)\n` +
      `• ${categories.length} expense categor${categories.length === 1 ? 'y' : 'ies'}\n` +
      `• All Schedule C mappings for these items\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Type "DELETE" to confirm.`
    );

    if (!confirmed) return;

    // Double confirmation
    const typedConfirmation = window.prompt(
      `To confirm deletion of ALL items from "${type.name}", type DELETE (in capital letters):`
    );

    if (typedConfirmation !== 'DELETE') {
      toast.info('Deletion cancelled.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      let deletedCount = 0;
      let errorCount = 0;

      toast.info(`Deleting ${totalItems} items from ${type.name}...`);

      // Delete all income sources
      for (const source of sources) {
        try {
          await axios.delete(`${API}/api/admin/employment-types/${type.id}/income-sources/${source.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          deletedCount++;
        } catch (err) {
          console.error('Error deleting source:', source.name, err);
          errorCount++;
        }
      }

      // Delete all expense categories
      for (const category of categories) {
        try {
          await axios.delete(`${API}/api/admin/employment-types/${type.id}/expense-categories/${category.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          deletedCount++;
        } catch (err) {
          console.error('Error deleting category:', category.name, err);
          errorCount++;
        }
      }

      if (errorCount > 0) {
        toast.warning(`Cleared ${deletedCount} items, ${errorCount} errors. Check console.`);
      } else {
        toast.success(`Successfully cleared all ${deletedCount} items from ${type.name}!`);
      }

      // Refresh data
      await fetchTypeIncomeSources(type.id);
      await fetchTypeExpenseCategories(type.id);
      await fetchScheduleCMappings(type.id, [], []);
      
    } catch (error) {
      console.error('Clear all error:', error);
      toast.error('Failed to clear items');
    }
  };

  const handleUploadEmploymentType = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show confirmation dialog
    const confirmed = window.confirm(
      `⚠️ Upload will ADD items to "${type.name}" (existing items will NOT be deleted).\n\n` +
      `File: ${file.name}\n\n` +
      `Do you want to continue?`
    );
    
    if (!confirmed) {
      e.target.value = '';
      return;
    }

    try {
      // Read the Excel file
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      
      // Get the first worksheet
      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];
      
      // Convert to JSON (array of arrays)
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      // Skip header row and empty rows
      const dataRows = jsonData.slice(1).filter(row => row && row.length >= 2 && row[0] && row[1]);
      
      if (dataRows.length === 0) {
        toast.error('No valid data found in file. Make sure columns are: Item Type, Item Name, Schedule C Line #');
        e.target.value = '';
        return;
      }
      
      toast.info(`Processing ${dataRows.length} rows for ${type.name}...`);

      const token = localStorage.getItem('token');
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i];
        const rowNum = i + 2; // +2 because we skipped header and Excel rows start at 1
        
        try {
          const [itemType, itemName, scheduleCLine] = row;
          
          const itemTypeStr = String(itemType).trim();
          const itemNameStr = String(itemName).trim();

          // Validate item type
          if (itemTypeStr !== 'income_source' && itemTypeStr !== 'expense_category') {
            errors.push(`Row ${rowNum}: Invalid item type "${itemTypeStr}" (must be "income_source" or "expense_category")`);
            errorCount++;
            continue;
          }

          // Add income source or expense category to this employment type
          if (itemTypeStr === 'income_source') {
            await axios.post(`${API}/api/admin/employment-types/${type.id}/income-sources`,
              { name: itemNameStr, is_common: false },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } else if (itemTypeStr === 'expense_category') {
            await axios.post(`${API}/api/admin/employment-types/${type.id}/expense-categories`,
              { name: itemNameStr, is_common: false },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          }

          // Add Schedule C mapping if provided
          if (scheduleCLine && String(scheduleCLine).trim()) {
            // Extract just the number from "Line 1" format
            const lineNumber = String(scheduleCLine).replace(/^Line\s*/i, '').trim();
            if (lineNumber) {
              try {
                // Find the description for this line from our Schedule C line items
                const categoryTypeForMapping = itemTypeStr === 'income_source' ? 'income' : 'expense';
                const lineItem = scheduleCLineItems.find(
                  item => item.line === lineNumber && item.category_type === categoryTypeForMapping
                );
                
                await axios.post(`${API}/api/schedule-c-mappings`,
                  {
                    category_type: categoryTypeForMapping,
                    category_name: itemNameStr,
                    schedule_c_line: lineNumber,
                    schedule_c_description: lineItem?.description || '',
                    employment_type_id: type.id  // Pass the employment type ID being uploaded for
                  },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
              } catch (mapErr) {
                // Mapping error is not critical, item was still added
                console.warn(`Row ${rowNum}: Item added but mapping failed`, mapErr);
              }
            }
          }

          successCount++;
        } catch (err) {
          const errorMsg = err.response?.data?.detail || err.message || 'Unknown error';
          errors.push(`Row ${rowNum}: ${errorMsg}`);
          errorCount++;
        }
      }

      // Show detailed results
      if (errorCount > 0) {
        console.error('Upload errors:', errors);
        toast.error(`${successCount} items added, ${errorCount} failed. Check console for details.`);
        // Show first few errors
        if (errors.length > 0) {
          const firstErrors = errors.slice(0, 3).join('\n');
          alert(`Upload Issues:\n\n${firstErrors}${errors.length > 3 ? `\n\n...and ${errors.length - 3} more errors` : ''}`);
        }
      } else {
        toast.success(`Upload complete! ${successCount} items added successfully.`);
      }
      
      // Refresh data for this employment type
      const sources = await fetchTypeIncomeSources(type.id);
      const categories = await fetchTypeExpenseCategories(type.id);
      await fetchScheduleCMappings(type.id, sources, categories);
      
      // Reset file input
      e.target.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Failed to upload file: ${error.message}`);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Employment Types</h2>
          <p className="text-slate-600">Manage employment types and their specific income sources & expense categories</p>
        </div>
        <Button 
          onClick={() => setShowCreateDialog(true)}
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Employment Type
        </Button>
      </div>

      {/* Employment Types List */}
      <div className="space-y-4">
        {employmentTypes.map((type) => (
          <Card key={type.id} className="overflow-hidden">
            <div 
              className="cursor-pointer hover:bg-slate-50 transition-colors px-4 py-3 border-b"
              onClick={() => handleTypeExpand(type.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Briefcase className="w-4 h-4 text-blue-600" />
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-base">{type.name}</span>
                    {type.description && (
                      <span className="text-sm text-slate-500">• {type.description}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditDialog(type);
                    }}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteDialog(type);
                    }}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                  {expandedType === type.id ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </div>
              </div>
            </div>

            {expandedType === type.id && (
              <CardContent className="space-y-6 pt-6">
                {/* Download/Upload Section */}
                <div className="flex items-center justify-between pb-4 border-b">
                  <p className="text-sm text-slate-600">Download or upload income sources, expense categories, and Schedule C mappings</p>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleDownloadEmploymentType(type)}
                      variant="outline"
                      size="sm"
                      className="gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Excel
                    </Button>
                    <Button 
                      onClick={() => document.getElementById(`upload-file-${type.id}`).click()}
                      variant="outline"
                      size="sm"
                      className="gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Upload Excel
                    </Button>
                    <Button 
                      onClick={() => handleDeleteAllMappings(type)}
                      variant="outline"
                      size="sm"
                      className="gap-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Mappings
                    </Button>
                    <Button 
                      onClick={() => handleClearAllItems(type)}
                      variant="destructive"
                      size="sm"
                      className="gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Clear Sources & Categories
                    </Button>
                    <input
                      id={`upload-file-${type.id}`}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => handleUploadEmploymentType(e, type)}
                      className="hidden"
                    />
                  </div>
                </div>

                {/* Income Sources Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <h3 className="font-semibold">Income Sources</h3>
                    </div>
                    <span className="text-xs text-slate-500">
                      <span className="text-green-600">✓</span> = Mapped to Schedule C
                    </span>
                  </div>
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add new income source..."
                      value={newSourceName[type.id] || ''}
                      onChange={(e) => setNewSourceName(prev => ({ ...prev, [type.id]: e.target.value }))}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddIncomeSource(type.id)}
                    />
                    <Button
                      onClick={() => handleAddIncomeSource(type.id)}
                      size="sm"
                      className="shrink-0"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {typeSources[type.id]?.length > 0 ? (
                      typeSources[type.id].map((source) => {
                        const isMapped = scheduleCMappings[type.id]?.some(
                          m => m.category_type === 'income' && m.category_name === source.name
                        );
                        return (
                          <Badge 
                            key={source.id}
                            variant="secondary"
                            className={`gap-1.5 py-1.5 px-3 ${isMapped ? 'bg-green-50 text-green-900 border-green-400 border-2' : ''}`}
                          >
                            {isMapped && (
                              <span className="text-green-600 font-bold" title="Mapped to Schedule C">✓</span>
                            )}
                            {source.name}
                            <button
                              onClick={() => handleDeleteIncomeSource(type.id, source.id)}
                              className="hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </Badge>
                        );
                      })
                    ) : (
                      <p className="text-sm text-slate-500 italic">No income sources yet</p>
                    )}
                  </div>
                </div>

                {/* Expense Categories Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b">
                    <div className="flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-red-600" />
                      <h3 className="font-semibold">Expense Categories</h3>
                    </div>
                    <span className="text-xs text-slate-500">
                      <span className="text-green-600">✓</span> = Mapped to Schedule C
                    </span>
                  </div>
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add new expense category..."
                      value={newCategoryName[type.id] || ''}
                      onChange={(e) => setNewCategoryName(prev => ({ ...prev, [type.id]: e.target.value }))}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddExpenseCategory(type.id)}
                    />
                    <Button
                      onClick={() => handleAddExpenseCategory(type.id)}
                      size="sm"
                      className="shrink-0"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {typeCategories[type.id]?.length > 0 ? (
                      typeCategories[type.id].map((category) => {
                        const isMapped = scheduleCMappings[type.id]?.some(
                          m => m.category_type === 'expense' && m.category_name === category.name
                        );
                        return (
                          <Badge 
                            key={category.id}
                            variant="secondary"
                            className={`gap-1.5 py-1.5 px-3 ${isMapped ? 'bg-green-50 text-green-900 border-green-400 border-2' : ''}`}
                          >
                            {isMapped && (
                              <span className="text-green-600 font-bold" title="Mapped to Schedule C">✓</span>
                            )}
                            {category.name}
                            <button
                              onClick={() => handleDeleteExpenseCategory(type.id, category.id)}
                              className="hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </Badge>
                        );
                      })
                    ) : (
                      <p className="text-sm text-slate-500 italic">No expense categories yet</p>
                    )}
                  </div>
                </div>

                {/* Schedule C Mappings Section */}
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center justify-between pb-2">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-purple-600" />
                      <h3 className="font-semibold">Schedule C Mappings</h3>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => openScheduleCDialog(type.id, 'income')}
                        size="sm"
                        variant="outline"
                        className="text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Map Income
                      </Button>
                      <Button
                        onClick={() => openScheduleCDialog(type.id, 'expense')}
                        size="sm"
                        variant="outline"
                        className="text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Map Expense
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {scheduleCMappings[type.id]?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border">
                          <thead>
                            <tr className="bg-slate-100 border-b">
                              <th className="text-left py-2 px-3 font-semibold text-xs">Type</th>
                              <th className="text-left py-2 px-3 font-semibold text-xs">Category Name</th>
                              <th className="text-left py-2 px-3 font-semibold text-xs">Schedule C Line</th>
                              <th className="text-left py-2 px-3 font-semibold text-xs">Description</th>
                              <th className="text-center py-2 px-3 font-semibold text-xs w-16">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {scheduleCMappings[type.id]
                              .sort((a, b) => {
                                // Sort by type (income first), then by category name
                                if (a.category_type !== b.category_type) {
                                  return a.category_type === 'income' ? -1 : 1;
                                }
                                return a.category_name.localeCompare(b.category_name);
                              })
                              .map((mapping, idx) => (
                                <tr key={idx} className="border-b hover:bg-slate-50">
                                  <td className="py-2 px-3">
                                    <Badge variant={mapping.category_type === 'income' ? 'default' : 'secondary'} className="text-xs">
                                      {mapping.category_type}
                                    </Badge>
                                  </td>
                                  <td className="py-2 px-3 font-medium">{mapping.category_name}</td>
                                  <td className="py-2 px-3 text-purple-600">Line {mapping.schedule_c_line}</td>
                                  <td className="py-2 px-3 text-slate-600 text-xs">{mapping.schedule_c_description}</td>
                                  <td className="py-2 px-3 text-center">
                                    <button
                                      onClick={() => handleDeleteScheduleCMapping(type.id, mapping.category_type, mapping.category_name)}
                                      className="hover:text-red-600 transition-colors p-1"
                                      title="Delete mapping"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 italic">No Schedule C mappings yet. Map income sources and expense categories to Schedule C lines for tax reporting.</p>
                    )}
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Employment Type</DialogTitle>
            <DialogDescription>
              Add a new employment type with custom income sources and expense categories
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateType} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Content Creator"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this employment type"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Employment Type</DialogTitle>
            <DialogDescription>
              Update the employment type details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateType} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description (Optional)</Label>
              <Input
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Update</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Employment Type</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedType?.name}"? This will also remove all associated income sources and expense categories.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteType}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule C Mapping Dialog */}
      <Dialog open={showScheduleCDialog} onOpenChange={setShowScheduleCDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Map to Schedule C</DialogTitle>
            <DialogDescription>
              Map {scheduleCFormData.category_type === 'income' ? 'income source' : 'expense category'} to IRS Schedule C line
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="category">
                {scheduleCFormData.category_type === 'income' ? 'Income Source' : 'Expense Category'}
              </Label>
              <Select
                value={scheduleCFormData.category_name}
                onValueChange={(value) => setScheduleCFormData({ ...scheduleCFormData, category_name: value })}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder={`Select ${scheduleCFormData.category_type}`} />
                </SelectTrigger>
                <SelectContent>
                  {scheduleCFormData.category_type === 'income' ? (
                    typeSources[selectedScheduleCItem?.typeId]?.map((source) => (
                      <SelectItem key={source.id} value={source.name}>
                        {source.name}
                      </SelectItem>
                    ))
                  ) : (
                    typeCategories[selectedScheduleCItem?.typeId]?.map((category) => (
                      <SelectItem key={category.id} value={category.name}>
                        {category.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="schedule-c-line">Schedule C Line</Label>
              <Select
                value={scheduleCFormData.schedule_c_line}
                onValueChange={(value) => {
                  const lines = scheduleCLineItems.filter(l => l.category_type === scheduleCFormData.category_type);
                  const selected = lines.find(l => l.line === value);
                  setScheduleCFormData({
                    ...scheduleCFormData,
                    schedule_c_line: value,
                    schedule_c_description: selected?.description || ''
                  });
                }}
              >
                <SelectTrigger id="schedule-c-line">
                  <SelectValue placeholder="Select Schedule C line" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {scheduleCLineItems
                    .filter(l => l.category_type === scheduleCFormData.category_type)
                    .map((line) => (
                      <SelectItem key={line.line} value={line.line}>
                        Line {line.line}: {line.description}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {scheduleCFormData.schedule_c_description && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>Line {scheduleCFormData.schedule_c_line}:</strong> {scheduleCFormData.schedule_c_description}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleCDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleAddScheduleCMapping(selectedScheduleCItem?.typeId)}>
              Add Mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
